import { useCallback, useEffect, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

const isRunningStandalone = (): boolean => {
  if (typeof window === 'undefined') return false
  const iosStandalone = Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone)
  return window.matchMedia('(display-mode: standalone)').matches || iosStandalone
}

let deferredPromptEvent: BeforeInstallPromptEvent | null = null
let appInstalled = false
let listenersBound = false
const subscribers = new Set<() => void>()

const notifySubscribers = () => {
  subscribers.forEach((subscriber) => subscriber())
}

const bindGlobalListeners = () => {
  if (listenersBound || typeof window === 'undefined') return

  appInstalled = isRunningStandalone()

  const handleBeforeInstallPrompt = (event: Event) => {
    const promptEvent = event as BeforeInstallPromptEvent
    promptEvent.preventDefault()
    if (!isRunningStandalone()) {
      deferredPromptEvent = promptEvent
      notifySubscribers()
    }
  }

  const handleAppInstalled = () => {
    appInstalled = true
    deferredPromptEvent = null
    notifySubscribers()
  }

  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.addEventListener('appinstalled', handleAppInstalled)
  listenersBound = true
}

export const useInstallAppPrompt = () => {
  const [, setSignal] = useState(0)
  const [isInstalling, setIsInstalling] = useState(false)

  useEffect(() => {
    bindGlobalListeners()

    const handleChange = () => {
      setSignal((value) => value + 1)
    }
    subscribers.add(handleChange)
    return () => {
      subscribers.delete(handleChange)
    }
  }, [])

  const installApp = useCallback(async (): Promise<boolean> => {
    if (!deferredPromptEvent || appInstalled) {
      return false
    }

    setIsInstalling(true)
    try {
      await deferredPromptEvent.prompt()
      const choice = await deferredPromptEvent.userChoice
      if (choice.outcome === 'accepted') {
        appInstalled = true
      }
      return choice.outcome === 'accepted'
    } finally {
      deferredPromptEvent = null
      setIsInstalling(false)
      notifySubscribers()
    }
  }, [])

  return {
    canInstall: Boolean(deferredPromptEvent) && !appInstalled,
    isInstalling,
    installApp,
    isInBrowserMode: !isRunningStandalone(),
  }
}
